import * as React from "react"

import { cn } from "@/lib/utils"

/**
 * The standing explainer at the foot of a wizard step: how the thing works,
 * what the file should contain, what happens when you press the button.
 *
 * ── WHY IT IS NOT AN `Alert` ────────────────────────────────────────────────
 * `Alert` carries `role="alert"`, which tells assistive technology that
 * something HAS HAPPENED or needs attention — an error, a warning, a state that
 * changed. A note that is always on screen and never changes is neither, and
 * announcing it on every visit to the step both wastes the user's attention and
 * dilutes the real alerts sitting beside it (a failed upload, a bank that did
 * not answer). So the split is by ROLE, not by wizard:
 *
 *   standing explainer  -> `StepNote`
 *   conditional notice  -> `Alert` (info / warning / destructive)
 *
 * ── WHY IT EXISTS AT ALL ────────────────────────────────────────────────────
 * Three import wizards had already hand-rolled these exact classes — CAMT's
 * "Supported formats", ESPM's "Where to export from", the utilities CSV's
 * "Expected columns" — and the bank-connection journey was about to write a
 * fourth and fifth. One component, so the next wizard inherits the convention
 * instead of copying a div.
 *
 * Children are rendered inside a `text-sm text-muted-foreground` column with a
 * `space-y-3` rhythm, so they need no colour or size classes of their own —
 * paragraphs, `<ul>`/`<ol>` lists and `<strong>` leads all sit right.
 */
export interface StepNoteProps
  // `title` is widened from the DOM attribute (a tooltip string) to a node, so
  // it has to be omitted before being redeclared.
  extends Omit<React.HTMLAttributes<HTMLDivElement>, "title"> {
  /** The note's heading. A short noun phrase, not a sentence. */
  title: React.ReactNode
}

const StepNote = React.forwardRef<HTMLDivElement, StepNoteProps>(
  ({ className, title, children, ...props }, ref) => (
    <div
      ref={ref}
      className={cn("bg-muted rounded-lg p-4 space-y-3", className)}
      {...props}
    >
      <h3 className="font-medium">{title}</h3>
      <div className="text-sm text-muted-foreground space-y-3">{children}</div>
    </div>
  )
)
StepNote.displayName = "StepNote"

export { StepNote }
